import { Fragment, MutableRefObject, useLayoutEffect, useRef } from 'react'

import { segmentMentions, MentionableEmployee } from 'src/lib/mentions.utils'

interface MentionHighlightBackdropProps {
  content: string
  recruiters: MentionableEmployee[] | null | undefined
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>
}

const MIRRORED_STYLES = [
  'font',
  'letterSpacing',
  'lineHeight',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
] as const

/**
 * A textarea cannot style part of its text, so this sits behind one inside a
 * positioned wrapper, mirrors its box and typography, and paints a highlight
 * under each @-mention the composer will turn into a tag on save. The text
 * itself stays transparent; the textarea on top draws the letters and caret.
 */
const MentionHighlightBackdrop = ({
  content,
  recruiters,
  textareaRef,
}: MentionHighlightBackdropProps) => {
  const backdropRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const textarea = textareaRef.current
    const backdrop = backdropRef.current
    if (!textarea || !backdrop) return

    const sync = () => {
      const computed = getComputedStyle(textarea)
      for (const property of MIRRORED_STYLES) {
        backdrop.style[property] = computed[property]
      }
      const parent = backdrop.offsetParent?.getBoundingClientRect()
      const box = textarea.getBoundingClientRect()
      backdrop.style.top = `${box.top - (parent?.top ?? 0)}px`
      backdrop.style.left = `${box.left - (parent?.left ?? 0)}px`
      // clientWidth leaves out a visible scrollbar, so lines wrap identically.
      backdrop.style.width = `${
        textarea.clientWidth +
        parseFloat(computed.borderLeftWidth) +
        parseFloat(computed.borderRightWidth)
      }px`
      backdrop.style.height = `${box.height}px`
      backdrop.scrollTop = textarea.scrollTop
    }

    sync()
    textarea.addEventListener('scroll', sync)
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync)
    observer?.observe(textarea)
    return () => {
      textarea.removeEventListener('scroll', sync)
      observer?.disconnect()
    }
  }, [textareaRef, content])

  return (
    <div
      ref={backdropRef}
      aria-hidden
      data-testid="note-mention-highlights"
      className="pointer-events-none absolute box-border overflow-hidden whitespace-pre-wrap break-words border-transparent text-transparent"
    >
      {segmentMentions(content, recruiters).map((segment, index) =>
        segment.mention ? (
          <mark
            key={index}
            className="rounded bg-doubleNickel-brand-50 text-transparent"
          >
            {segment.text}
          </mark>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        )
      )}
      {/* Keeps a trailing newline from collapsing, so wrapping matches. */}
      {'​'}
    </div>
  )
}

export default MentionHighlightBackdrop
